import { type UserList, getUserName } from 'models';
import { usePermissions } from 'permissions';
import { BriefcaseLineIcon, Col, CommentLineIcon, MailLineIcon, Row, Space, Tag, Text } from 'ui';
import { isMongoObjectId } from 'common/utils';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { DirectMessageButton } from 'features/chat/direct-message-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { DirectMessageButtonNode } from 'features/chat/direct-message-button-node';
import { useGetUserFirstNameWithFallback, useGetUserLastNameWithFallback } from '../../hooks';
import { AdminUserDetailButton } from '../admin-user-detail-button';
import { AvatarLink } from '../avatar-link';
import { EhrUserDetailButton } from '../ehr-user-detail-button';
import { UserNameLink } from '../user-name-link';
import { QuickViewCardSkeleton } from './quick-view-card-skeleton';

export type QuickViewCardUser = { profilePhoto?: string | UserList['profilePhoto'] } & Partial<
  Pick<UserList, 'email' | 'firstName' | 'lastName' | 'nodeUserId' | 'profession'>
> &
  Pick<UserList, 'id'>;

type P = { isLoading: false; user: QuickViewCardUser } | { isLoading: true };

const QuickViewUserCard = (props: P) => {
  const { nodeUserData: nodeViewer } = useUser();
  const { isLoading } = props;
  const { canViewAdminDashboard, canViewEhrPortal } = usePermissions();
  const getUserFirstNameWithFallback = useGetUserFirstNameWithFallback();
  const getUserLastNameWithFallback = useGetUserLastNameWithFallback();

  if (isLoading) {
    return <QuickViewCardSkeleton />;
  }
  const { user } = props;
  const { email, firstName, id, lastName, nodeUserId, profession, profilePhoto } = user;
  const fullName = getUserName({ firstName, lastName });

  return (
    <Space block orientation="vertical" size="middle">
      <Row gutter="ms">
        <Col>
          <AvatarLink avatar={{ photo: profilePhoto, size: 80 }} userId={id}>
            {fullName}
          </AvatarLink>
        </Col>
        <Col flex="1">
          <Space orientation="vertical" size="small">
            <UserNameLink userId={id}>{fullName}</UserNameLink>

            <Space orientation="vertical" size="middle">
              {Boolean(email) || Boolean(profession) ? (
                <Space orientation="vertical" size="xxs">
                  {email ? (
                    <Tag color="bg-none" icon={<MailLineIcon />} padding={false}>
                      <Text size="small">{email}</Text>
                    </Tag>
                  ) : null}

                  {profession ? (
                    <Tag color="bg-none" icon={<BriefcaseLineIcon />} padding={false}>
                      <Text size="small">{profession.name}</Text>
                    </Tag>
                  ) : null}
                </Space>
              ) : null}
            </Space>
          </Space>
        </Col>
      </Row>
      <Space block>
        {isMongoObjectId(id) && email ? (
          <DirectMessageButtonNode
            icon={<CommentLineIcon />}
            size="middle"
            type="primary"
            user={{
              _id: id,
              email,
              firstName: getUserFirstNameWithFallback(user),
              lastName: getUserLastNameWithFallback(user),
            }}
            viewer={nodeViewer}
          >
            Send message
          </DirectMessageButtonNode>
        ) : (
          <DirectMessageButton icon={<CommentLineIcon />} legacyUserId={id} size="middle" type="primary">
            Send message
          </DirectMessageButton>
        )}
        {/* id may be a Mongo ObjectId(node) only if the query in UserPopoverContentWithQuery fails and a fallback is used */}
        {!isMongoObjectId(id) && canViewAdminDashboard({}) ? (
          <AdminUserDetailButton size="middle" tooltip="Admin console" userId={id} />
        ) : null}
        {canViewEhrPortal({}) ? (
          <EhrUserDetailButton size="middle" tooltip="EHR Portal" userId={isMongoObjectId(id) ? id : nodeUserId} />
        ) : null}
      </Space>
    </Space>
  );
};

export default QuickViewUserCard;
